import { ArrowDownIcon } from "@App/Components/common/Icons";
import { HintModal } from "@App/Components/HintModal";
import { GarageMap } from "@App/Components/molecules/common/map/GarageMap";
import { NoGarageFound } from "@App/Components/NoGarageFound";
import { NonBookableHintModal } from "@App/Components/NonBookableHintModal";
import { ServiceFAQs } from "@App/Components/ServiceFAQs";
import { GarageListItemSkeleton } from "@App/Components/Skeletons/GarageListItemSkeleton";
import { useDeviceDimensions } from "@App/hooks/useDeviceDimensions";
import { actionCreators, type State } from "@App/state";
import type { Garage, PaginatorInfo, Service } from "@App/types";
import { SortOrderType } from "@App/types/enums";
import { hasItems, safeFilter, safeMap } from "@App/utils/array";
import {
  OrganizationIsAthlonAndSortByIsPartnerRecommended,
  OrganizationIsNeitherRepareoNorDBConnect,
  OrganizationIsRepareoAndServiceIsInspection,
} from "@App/utils/conditionals/combinations";
import { GarageIsAuthorizedWorkshop, GarageIsBookable } from "@App/utils/conditionals/garages";
import {
  OrganizationIsAthlon,
  OrganizationIsALD,
  OrganizationIsFinn,
  OrganizationIsMARA,
  OrganizationIsMFS,
  OrganizationIsRepareo,
} from "@App/utils/conditionals/organization";
import { NoBookableGarage } from "@App/Components/ClientSpecific/Finn/NoBookableGarage";
import { DefaultSortingIsSmallestDistance } from "@App/utils/conditionals/sorting";
import { bindActionCreators } from "@reduxjs/toolkit";
import { clearAllBodyScrollLocks, disableBodyScroll } from "body-scroll-lock";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GarageListItem } from "@App/Components/GarageListItem/GarageListItem";
import { PremiumServicesIncludeTechnicalRepair } from "@App/utils/conditionals/services";
import { isAfter } from "date-fns";
import { TechnicalRepairGarageListHint } from "@App/Components/atoms/whitelabel/hints/TechnicalRepairGarageListHint";
import { RedirectToMC } from "@App/Components/ClientSpecific/MARA/RedirectToMC";

type RecommendedGarageEntry = {
  garage: Garage;
  flags: Partial<Record<string, boolean>>;
};

type GarageListProps = {
  toggleMap: boolean;
  onToggleMap: (open: boolean) => void;
  service?: Service;
  onChange: (garage: Garage) => void;
  mobileServiceFAQs?: boolean;
  useGarages: {
    loading: boolean;
    fetchMoreLoading: boolean;
    garages: Garage[] | null;
    recommendedGarages: Garage[];
    paginatorInfo: PaginatorInfo | null;
    isFiltered: boolean;
    earliestAvailableGarage: Garage | null;
    bestReviewedGarage: Garage | null;
    nearestGarage: Garage | null;
    authorizedGarage: Garage | null;
    error?: Error | undefined;
    refetch: () => void;
    loadMore: () => void;
  };
};

export const GarageList = React.memo<GarageListProps>((props: GarageListProps) => {
  const organization = useSelector((state: State) => state.orders.organization);
  const place = useSelector((state: State) => state.orders.place);
  const service = useSelector((state: State) => state.orders.service);
  const sortBy = useSelector((state: State) => state.filters.sortBy);
  const brand = useSelector((state: State) => state.filters.brand);
  const premiumServices = useSelector((state: State) => state.orders.premiumServices);
  const showAsNonBookable = useSelector((state: State) => state.filters.showAsNonBookable);
  const warrantyEndDate = useSelector(
    (state: State) => state.auth?.driverData?.vehicle?.warrantyEndDate,
  );

  const { isMobile } = useDeviceDimensions();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { changeSortByFilter } = bindActionCreators(actionCreators, dispatch);

  const [isInitialGarageList, setIsInitialGarageList] = useState(true);

  useEffect(() => {
    if (props.toggleMap && isMobile) {
      window.scrollTo(0, 0);
      disableBodyScroll(document.querySelector("#garage-map"));
    } else {
      clearAllBodyScrollLocks();
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [props.toggleMap, isMobile]);

  const {
    loading,
    fetchMoreLoading,
    garages,
    paginatorInfo,
    isFiltered,
    recommendedGarages,
    earliestAvailableGarage,
    bestReviewedGarage,
    nearestGarage,
    authorizedGarage,
    error,
    refetch,
    loadMore,
  } = props.useGarages;

  // refetch if user used the browser back button
  useEffect(() => {
    if (!loading) {
      refetch();
    }
  }, []);

  useEffect(() => {
    if (
      isInitialGarageList &&
      DefaultSortingIsSmallestDistance(organization) &&
      safeFilter(garages, (garage) => garage.pivot?.preferred_partner).length > 0
    ) {
      changeSortByFilter(SortOrderType.RECOMMENDED);
      setIsInitialGarageList(false);
    }
  }, [garages]);

  if (error) {
    console.log(error);
    return <p>{t("error")}</p>;
  }

  // garages starts as null, show loading while initial fetch is running
  if (loading || !garages || !service) {
    return (
      <>
        {[...Array(15)].map((e, i) => (
          <GarageListItemSkeleton key={i} />
        ))}
      </>
    );
  }

  const renderGarageListItem = (
    garage: Garage,
    highlighted: boolean,
    flags: Partial<Record<string, boolean>>,
  ) => (
    <GarageListItem
      key={`recommended-${garage.id}`}
      service={service}
      garage={garage}
      showAsNonBookable={showAsNonBookable}
      prefix={service.garage_prefix}
      onClick={props.onChange}
      earliestAvailable={flags.earliestAvailable}
      nearest={flags.nearest}
      bestReviewed={flags.bestReviewed}
      authorized={flags.authorized}
      preferredPartner={flags.preferredPartner}
      highlighted={highlighted}
    />
  );

  const showRecommendedGarageElements =
    !isFiltered &&
    (sortBy === SortOrderType.RECOMMENDED ||
      OrganizationIsAthlonAndSortByIsPartnerRecommended(organization, sortBy as SortOrderType));

  /**
   * The garages of the recommended block, in render order and without duplicates — a preferred
   * partner can also be the nearest or best reviewed garage.
   */
  const getRecommendedGaragesWithFlags = (): RecommendedGarageEntry[] => {
    if (!showRecommendedGarageElements || !garages || garages.length <= 0) return [];

    const entries: RecommendedGarageEntry[] = safeMap(recommendedGarages, (garage) => ({
      garage,
      flags: { preferredPartner: OrganizationIsAthlon(organization) },
    }));

    if (OrganizationIsRepareo(organization)) {
      const repareoTips: { garage: Garage | null; flags: Partial<Record<string, boolean>> }[] = [
        { garage: authorizedGarage, flags: { authorized: true } },
        { garage: nearestGarage, flags: { nearest: true } },
        { garage: bestReviewedGarage, flags: { bestReviewed: true } },
        { garage: earliestAvailableGarage, flags: { earliestAvailable: true } },
      ];

      for (const { garage, flags } of repareoTips) {
        if (!garage || entries.some((entry) => entry.garage.id === garage.id)) continue;
        entries.push({ garage, flags });
      }
    }

    return entries;
  };

  const recommendedGaragesWithFlags = getRecommendedGaragesWithFlags();

  const getRecommendedGarageElements = () => (
    <>
      {recommendedGaragesWithFlags.map(({ garage, flags }) =>
        renderGarageListItem(garage, true, flags),
      )}
    </>
  );

  const getGarages = () => {
    if (!garages) return null;

    // garages of the recommended block are already rendered above, don't repeat them here
    const recommendedGarageIds = new Set(
      recommendedGaragesWithFlags.map(({ garage }) => garage.id),
    );
    const remainingGarages = safeFilter(garages, (garage) => !recommendedGarageIds.has(garage.id));

    return safeMap(remainingGarages, (garage, idx) => (
      <Fragment key={`garage-fragment-${garage.id}`}>
        {idx === 4 && service && (service?.pivot?.faqs?.length ?? 0) > 0 && (
          <div className={"md:hidden"}>
            <span className={"flex justify-center items-center text-neutral-700 text-xs py-2"}>
              <ArrowDownIcon className={"w-5 h-5 mr-3"} /> Mehr Werkstätten unterhalb
            </span>
            <ServiceFAQs sidebar />
          </div>
        )}
        {/* Add a separator when the last bookable garage is displayed to differentiate between bookable and non-bookable garages*/}
        {[SortOrderType.BEST_REVIEWED, SortOrderType.EARLIEST_SLOT].includes(
          sortBy as SortOrderType,
        ) &&
          GarageIsBookable(remainingGarages[idx - 1]) &&
          !GarageIsBookable(garage) && (
            <div className="text-lg font-bold font-serif mt-6 mb-3">
              {t("listHints.furtherGaragesSortedByDistance")}
            </div>
          )}
        <GarageListItem
          key={`garage-${garage.id}`}
          service={service}
          garage={garage}
          showAsNonBookable={showAsNonBookable}
          prefix={service.garage_prefix}
          onClick={props.onChange}
          earliestAvailable={garage === earliestAvailableGarage}
          nearest={garage === nearestGarage}
          bestReviewed={garage === bestReviewedGarage}
          authorized={
            (brand?.id && garage?.authorizedBrands?.some((b) => b.id === brand.id)) || false
          }
        />
      </Fragment>
    ));
  };

  const modalAlreadyClicked = () => {
    try {
      return sessionStorage.getItem("HintModal") !== "closed";
    } catch (error) {
      return true;
    }
  };

  // Show no garage found if garages are empty
  if (!loading && garages.length === 0) {
    return (
      <div className="w-full">
        {OrganizationIsFinn(organization) ? <NoBookableGarage /> : <NoGarageFound />}
        {OrganizationIsMARA(organization) && <RedirectToMC />}
      </div>
    );
  }

  return (
    <div className="w-full">
      {modalAlreadyClicked() && (
        <Fragment>
          {OrganizationIsALD(organization) &&
            PremiumServicesIncludeTechnicalRepair(premiumServices) &&
            sortBy === SortOrderType.PARTNER_RECOMMENDED && (
              <TechnicalRepairGarageListHint
                warrantyEnded={
                  warrantyEndDate ? isAfter(new Date(), new Date(warrantyEndDate)) : true
                }
                firstGarageIsOem={hasItems(garages) && GarageIsAuthorizedWorkshop(garages[0])}
              />
            )}
          {OrganizationIsNeitherRepareoNorDBConnect(organization) &&
            !OrganizationIsMFS(organization) &&
            !OrganizationIsALD(organization) && (
              <HintModal
                text={t("hint.text", {
                  organization: organization?.name?.toUpperCase(),
                })}
                service={service}
                organisation={organization}
              />
            )}

          {OrganizationIsRepareoAndServiceIsInspection(organization, service) && (
            <HintModal
              text={t("hint.repareo-inspection-text")}
              service={service}
              organisation={organization}
            />
          )}
          {showAsNonBookable && <NonBookableHintModal text={t("listHints.shownAsNonBookable")} />}
        </Fragment>
      )}

      {props.toggleMap && (
        <div className="mb-3 w-full">
          <GarageMap
            center={{
              lat: place?.lat ?? 52.520008,
              lng: place?.lon ?? 13.404954,
            }} // use berlin if no place is set
            garages={garages}
            service={service}
            onClose={() => props.onToggleMap(false)}
            onSelectGarage={props.onChange}
            zoom={11}
          />
        </div>
      )}
      {sortBy === SortOrderType.RECOMMENDED && !isFiltered && (
        <>
          <div className="text-lg font-bold font-serif mb-3">{t("listHints.ourTip")}</div>
          {getRecommendedGarageElements()}
          <div className="text-lg font-bold font-serif mt-6 mb-3">
            {t("listHints.sortedByDistance")}
          </div>
        </>
      )}
      {OrganizationIsAthlonAndSortByIsPartnerRecommended(organization, sortBy as SortOrderType) &&
        !isFiltered && <>{getRecommendedGarageElements()}</>}
      {getGarages()}
      {paginatorInfo?.hasMorePages && (
        <div className="w-full flex justify-center">
          <button
            disabled={fetchMoreLoading}
            className={`bg-warning text-white w-full py-3 px-4 rounded mt-3 font-bold md:max-w-[340px] ${fetchMoreLoading ? "animate-pulse" : ""}`}
            onClick={loadMore}
          >
            {fetchMoreLoading ? "Lädt..." : "Mehr Werkstätten anzeigen"}
          </button>
        </div>
      )}
    </div>
  );
});
